import { OTHER_OPTION_VALUE } from "../constants/app-constant";
import { BuiltSectionInput, DiagnosisSplitResult } from "../types";

export const extractDiagnosisOptions = (
  input: string[]
): DiagnosisSplitResult => {
  const otherPrefix = OTHER_OPTION_VALUE;
  let otherText: string | null = null;

  const selectedOptions = input?.map((item) => {
    const trimmed = item?.trim();

    if (trimmed?.startsWith(otherPrefix)) {
      const match = trimmed?.match(/\{(.*)\}/);
      if (match) {
        otherText = match[1]?.trim();
      }
      return otherPrefix; // retain just the label
    }

    return trimmed;
  });

  return { selectedOptions, otherText };
};

export function parseOptions(input: string = ""): {
  options: string[];
  hasOtherOption: boolean;
} {
  const allOptions = input?.split("$").filter(Boolean) || [];

  const filteredOptions: string[] = [];
  let hasOtherOption = false;

  for (const option of allOptions) {
    if (option?.trim()?.toLowerCase()?.startsWith("other")) {
      hasOtherOption = true;
    } else {
      filteredOptions?.push(option.trim());
    }
  }

  return {
    options: filteredOptions,
    hasOtherOption,
  };
}

export function buildSections(input: BuiltSectionInput) {
  const labelMap: Record<string, string> = {
    SSV: input.SIGN_LAB,
    LIF: input.LABEL_LAB,
    TM: input.TREAT_LAB,
  };

  const sectionData: Record<
    string,
    { title: string; items: { label: string; date: string; value?: string }[] }
  > = {};

  const indicators = input?.CLINICAL_INDICATORS?.split(";")?.filter(Boolean);

  indicators?.forEach((item) => {
    const dateMatch = item?.match(/\(\d{2}\/\d{2}\/\d{2}\)\s*$/);
    const date = dateMatch ? dateMatch[0] : "";

    const codeMatch = item?.match(/\{([^{}]+)\}/);
    const sectionCode = codeMatch ? codeMatch[1] : "UNKNOWN";

    const valueMatch = item?.match(/=\s*<strong>(.*?)<\/strong>/i);
    const value = valueMatch ? valueMatch[1].trim() : undefined;

    const label = item
      ?.replace(/\{[^{}]*\}/, "") // remove {CODE}
      ?.replace(date, "") // remove date
      ?.replace(/=\s*<strong>.*?<\/strong>/i, "") // remove value part
      ?.trim();

    const title = labelMap[sectionCode] || "Unknown Section";

    if (!sectionData[sectionCode]) {
      sectionData[sectionCode] = {
        title,
        items: [],
      };
    }

    sectionData[sectionCode].items.push({
      label,
      date,
      ...(value ? { value } : {}),
    });
  });

  return Object?.keys(labelMap)
    ?.filter((code) => sectionData[code])
    ?.map((code) => sectionData[code]);
}

export const otherFieldCheck = (
  state: string | string[],
  value: string
): string => {
  const otherLabel = OTHER_OPTION_VALUE;

  if (typeof state === "string") {
    const updated = state === otherLabel ? `${otherLabel}{${value}}` : state;
    return `$${updated}`;
  }

  const updatedArray = state?.map((item) =>
    item === otherLabel ? `${otherLabel}{${value}}` : item
  );

  return updatedArray?.map((item) => `$${item}`).join(" ");
};

export function convertMultipleDocumentationHTML(inputHtml: string): string {
  const topLevelBlocks = inputHtml?.split(/\s*;\s*/).filter(Boolean);
  const finalSections: string[] = [];

  topLevelBlocks?.forEach((blockHtml) => {
    const parser = new DOMParser();
    const doc = parser?.parseFromString(blockHtml, "text/html");
    const sectionLines: string[] = [];

    // 1. Extract <b> tag (note heading)
    const heading = doc?.querySelector("b")?.outerHTML;
    if (heading) sectionLines.push(heading);

    // 2. Extract <label> <span><i> entries
    const spans = doc?.querySelectorAll("span.abc");

    spans?.forEach((span) => {
      const prevTextNode = span?.previousSibling;
      let label = "";

      if (
        prevTextNode &&
        prevTextNode.nodeType === Node.TEXT_NODE &&
        prevTextNode.textContent?.trim()
      ) {
        label = prevTextNode.textContent.trim();
        sectionLines?.push(`<p><strong>${label}</strong></p>`);
      }

      const iTag = span?.querySelector("i");
      if (iTag) {
        const items = iTag.innerHTML
          ?.split(/[,;]+/)
          ?.map((s) => s.trim().replace(/^"(.*)"$/, "$1"))
          ?.filter(Boolean)
          ?.map((item) => `<li>${item}</li>`)
          ?.join("\n");

        sectionLines?.push(`<ul>${items}</ul>`);
      }
    });

    finalSections?.push(sectionLines.join("\n"));
  });

  return finalSections?.join("\n<hr/>\n");
}
